import { useParams } from "react-router-dom";
import { Link } from "react-router-dom";
import { data, paintingData, portraitData, sculptureData, graphicDesgignData } from "../data";
import Reveal from "../components/Reveal";

function ProjectDetail({ items, breadcrumb = "Mural Arts" }) {
  const { id } = useParams();
  const cardId = parseInt(id, 10);
  const filteredData = items.find((p) => p.id === cardId);

  if (!filteredData) {
    return (
      <main className="bg-[var(--bg)] text-[var(--bone)] pt-40 pb-24 px-5 md:px-10">
        <div className="max-w-7xl mx-auto">
          <h1 className="font-display text-5xl text-[var(--bone)]">
            Project not found
          </h1>
          <Link
            to="/ourwork"
            className="mt-8 inline-block text-[var(--clay)] link-underline"
          >
            ← Back to all work
          </Link>
        </div>
      </main>
    );
  }

  // Build gallery from img1..img5
  const gallery = [filteredData.img1, filteredData.img2, filteredData.img3, filteredData.img4, filteredData.img5].filter(Boolean);

  return (
    <main className="bg-[var(--bg)] text-[var(--bone)] pt-32 pb-24">
      {/* BREADCRUMB */}
      <section className="px-5 md:px-10">
        <div className="max-w-7xl mx-auto">
          <Reveal>
            <div className="flex items-center gap-3 text-[10px] uppercase tracking-[0.32em] text-[var(--muted)]">
              <Link to="/" className="hover:text-[var(--bone)] transition-colors">
                Home
              </Link>
              <span>/</span>
              <Link to="/ourwork" className="hover:text-[var(--bone)] transition-colors">
                {breadcrumb}
              </Link>
              <span>/</span>
              <span className="text-[var(--clay)]">{filteredData.title}</span>
            </div>
          </Reveal>
        </div>
      </section>

      {/* HERO */}
      <section className="px-5 md:px-10 mt-10">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-12 gap-6 items-end">
            <div className="col-span-12 md:col-span-8">
              <Reveal>
                <h1 className="font-display font-light text-[clamp(2.8rem,7vw,6.5rem)] leading-[0.95] tracking-tight">
                  {filteredData.title}
                </h1>
              </Reveal>
            </div>
            <div className="col-span-12 md:col-span-4">
              <Reveal delay={120}>
                <div className="flex flex-wrap gap-2 text-[10px] uppercase tracking-[0.32em]">
                  <span className="px-3 py-1 border border-[var(--line-strong)] rounded-full text-[var(--bone)]">
                    {breadcrumb}
                  </span>
                  <span className="px-3 py-1 border border-[var(--line-strong)] rounded-full text-[var(--muted)]">
                    Hand-painted
                  </span>
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* HERO IMAGE */}
      <section className="px-5 md:px-10 mt-10">
        <div className="max-w-7xl mx-auto">
          <Reveal>
            <div className="relative aspect-[16/9] md:aspect-[21/9] overflow-hidden bg-[var(--bg-elevated)] editorial-frame">
              <img
                src={filteredData.img1}
                alt={filteredData.title}
                className="w-full h-full object-cover"
              />
              <span className="absolute top-3 left-3 w-3 h-3 border-t border-l border-[var(--clay)]" />
              <span className="absolute top-3 right-3 w-3 h-3 border-t border-r border-[var(--clay)]" />
              <span className="absolute bottom-3 left-3 w-3 h-3 border-b border-l border-[var(--clay)]" />
              <span className="absolute bottom-3 right-3 w-3 h-3 border-b border-r border-[var(--clay)]" />
            </div>
          </Reveal>
        </div>
      </section>

      {/* INFO + SECONDARY IMG */}
      <section className="px-5 md:px-10 mt-16 md:mt-24">
        <div className="max-w-7xl mx-auto grid grid-cols-12 gap-6">
          <div className="col-span-12 md:col-span-4">
            <Reveal>
              <div className="text-[10px] uppercase tracking-[0.32em] text-[var(--clay)]">
                — About this wall
              </div>
            </Reveal>
          </div>
          <div className="col-span-12 md:col-span-8">
            <Reveal delay={120}>
              <div className="font-display text-[clamp(1.3rem,2.2vw,1.8rem)] leading-[1.4] text-[var(--bone)] space-y-5">
                {filteredData.info ? (
                  filteredData.info
                ) : filteredData.cardtext ? (
                  <p>{filteredData.cardtext}</p>
                ) : (
                  <p className="text-[var(--muted)]">
                    A painted wall, a real place, a story that belongs to its
                    neighbourhood.
                  </p>
                )}
              </div>
            </Reveal>
            <Reveal delay={240}>
              <div className="mt-10">
                <Link
                  to="/booknow"
                  className="group inline-flex items-center gap-3 px-6 py-3.5 bg-[var(--clay)] text-[var(--bone)] rounded-full text-sm tracking-wide hover:bg-[var(--bone)] hover:text-[var(--ink)] transition-colors"
                >
                  Commission a wall like this
                  <span className="relative w-4 h-4 overflow-hidden">
                    <span className="absolute inset-0 transition-transform duration-500 group-hover:translate-x-5 group-hover:-translate-y-5">
                      →
                    </span>
                    <span className="absolute inset-0 -translate-x-5 translate-y-5 transition-transform duration-500 group-hover:translate-x-0 group-hover:translate-y-0">
                      →
                    </span>
                  </span>
                </Link>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* GALLERY */}
      {gallery.length > 1 && (
        <section className="px-5 md:px-10 mt-16 md:mt-24">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-baseline justify-between mb-8">
              <Reveal>
                <h2 className="font-display font-light text-3xl md:text-5xl text-[var(--bone)]">
                  On the <span className="italic font-extralight">wall</span>
                </h2>
              </Reveal>
              <div className="text-[10px] uppercase tracking-[0.32em] text-[var(--muted)]">
                {gallery.length} frames
              </div>
            </div>

            <div className="grid grid-cols-12 gap-3 md:gap-4">
              {gallery.map((src, i) => (
                <Reveal
                  key={i}
                  delay={(i % 6) * 60}
                  className={
                    i === 0
                      ? "col-span-12 md:col-span-8"
                      : i === 1
                      ? "col-span-12 md:col-span-4"
                      : i === 2
                      ? "col-span-6 md:col-span-5"
                      : i === 3
                      ? "col-span-6 md:col-span-7"
                      : "col-span-12"
                  }
                >
                  <div
                    className={`relative overflow-hidden bg-[var(--bg-elevated)] editorial-frame ${
                      i === 0
                        ? "aspect-[16/10]"
                        : i === 1
                        ? "aspect-[4/5]"
                        : i === 2
                        ? "aspect-[4/3]"
                        : i === 3
                        ? "aspect-[16/10]"
                        : "aspect-[16/9]"
                    }`}
                  >
                    <img
                      src={src}
                      alt={`${filteredData.title} — frame ${i + 1}`}
                      loading="lazy"
                      className="w-full h-full object-cover"
                    />
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="px-5 md:px-10 mt-24 md:mt-40">
        <div className="max-w-7xl mx-auto border-t border-[var(--line)] pt-12 flex flex-col md:flex-row items-start md:items-end justify-between gap-6">
          <div>
            <div className="text-[10px] uppercase tracking-[0.32em] text-[var(--clay)]">
              — More walls
            </div>
            <h3 className="mt-3 font-display text-3xl md:text-5xl text-[var(--bone)] leading-tight max-w-xl">
              Keep walking the gallery.
            </h3>
          </div>
          <div className="flex flex-wrap gap-3">
            <Link
              to="/gallery"
              className="group inline-flex items-center gap-3 px-6 py-3.5 border border-[var(--line-strong)] text-[var(--bone)] rounded-full text-sm tracking-wide hover:border-[var(--bone)] transition-colors"
            >
              Open the gallery
            </Link>
            <Link
              to="/ourwork"
              className="group inline-flex items-center gap-3 px-6 py-3.5 bg-[var(--clay)] text-[var(--bone)] rounded-full text-sm tracking-wide hover:bg-[var(--bone)] hover:text-[var(--ink)] transition-colors"
            >
              All murals
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="M5 12h14M13 5l7 7-7 7" />
              </svg>
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
}

// Mural Arts (default)
export default function ProjectDetails() {
  return <ProjectDetail items={data} breadcrumb="Mural Arts" />;
}

export const GraphicWorkDetails = () => {
  return <ProjectDetail items={graphicDesgignData} breadcrumb="Graphic Design" />;
};

export const SculptureWorkDetails = () => {
  return <ProjectDetail items={sculptureData} breadcrumb="Sculpture" />;
};

export const PaintingWorkDetails = () => {
  return <ProjectDetail items={paintingData} breadcrumb="Painting" />;
};

export const PortraitWorkDetails = () => {
  return <ProjectDetail items={portraitData} breadcrumb="Portrait" />;
};